/**
 * The tab dock stopped taking taps now and then on iOS while the page still scrolled (Sam, 2026-09-28).
 *
 * The app never scrolls the document: body is position:fixed and <main> is the scroller. But WKWebView scrolls its
 * own scroll view to lift a focused input above the keyboard, and does not always scroll it back when the keyboard
 * closes (route change with an autofocused search field, a modal restoring focus or window.scrollTo(0, scrollY)).
 * With the document left offset, fixed elements still paint in place but WebKit hit-tests them at the offset
 * position, so taps on the dock land on <main> behind it. Nothing ever reset it.
 *
 * So: once no text field has focus - and the keyboard is therefore down - put the document back at 0,0. Checked on
 * focusout (after focus settles), on visualViewport resize (the keyboard's own signal), and on resume.
 */

import { Capacitor } from '@capacitor/core';
import { appLog } from './appLog';

function isTextEntry(el) {
  if (!el || el === document.body) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  const type = (el.getAttribute('type') || 'text').toLowerCase();
  return ![
    'button',
    'checkbox',
    'radio',
    'range',
    'submit',
    'reset',
    'file',
    'color',
    'image',
  ].includes(type);
}

/** The tallest visual viewport seen with no field focused: the screen without a keyboard. */
let openHeight = 0;
function noteOpenHeight() {
  const vv = window.visualViewport;
  if (vv && !isTextEntry(document.activeElement)) openHeight = Math.max(openHeight, vv.height);
}

/** The keyboard is up when the visual viewport is clearly shorter than the layout viewport - or than the screen was
 *  before any field had focus: on iOS the layout viewport shrank with it, so the first test alone said "down" with the
 *  keyboard up, and the reset scrolled the zip / phone / Instagram field out from under it (app_logs, /profile/edit,
 *  active INPUT, 2026-10-30). */
function keyboardLikelyUp() {
  const vv = window.visualViewport;
  if (!vv) return false;
  return window.innerHeight - vv.height > 80 || openHeight - vv.height > 80;
}

/** Everything WebKit may have left offset: layout scroll AND the visual viewport pan it uses for the keyboard. */
export function documentOffsets() {
  const vv = window.visualViewport;
  return {
    scrollX: window.scrollX,
    scrollY: window.scrollY,
    docScrollTop: document.documentElement.scrollTop,
    bodyScrollTop: document.body.scrollTop,
    vvOffsetTop: vv?.offsetTop ?? 0,
    vvOffsetLeft: vv?.offsetLeft ?? 0,
    vvPageTop: vv?.pageTop ?? 0,
  };
}

export function resetDocumentScrollIfIdle(reason = 'check') {
  if (typeof window === 'undefined') return false;
  // A pinch-zoomed page has a visual-viewport offset on purpose; leave it alone.
  if ((window.visualViewport?.scale ?? 1) > 1.01) return false;
  // A focused field with the keyboard up is the one time the offset is wanted. A focused field with the keyboard
  // DOWN (resume, a field left mounted behind another view) used to skip the reset for good (2026-10-04).
  if (isTextEntry(document.activeElement) && keyboardLikelyUp()) return false;
  const o = documentOffsets();
  const offset =
    o.scrollX ||
    o.scrollY ||
    o.docScrollTop ||
    o.bodyScrollTop ||
    o.vvOffsetTop ||
    o.vvOffsetLeft ||
    o.vvPageTop;
  if (!offset) return false;
  // Each reset is the dock bug caught in the act - app_logs says how often, and on which page.
  appLog('info', 'ui.scroll_reset', {
    ...o,
    reason,
    active: document.activeElement?.tagName ?? null,
  });
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  return true;
}

let installed = false;

/** Idempotent; returns a remover (used by tests and hot reload). */
export function installKeyboardScrollReset() {
  if (installed || typeof window === 'undefined') return () => {};
  // The offset is a WKWebView habit; a web visitor who pinch-zooms must not be snapped back every 3 s.
  if (!Capacitor.isNativePlatform()) return () => {};
  installed = true;
  noteOpenHeight();
  let timer = null;
  const soon = (reason) => () => {
    if (timer != null) clearTimeout(timer);
    // Focus moving input -> input fires focusout then focusin; wait for it to land, and for the keyboard to finish.
    timer = setTimeout(() => {
      timer = null;
      resetDocumentScrollIfIdle(reason);
    }, 350);
  };
  const onFocusOut = soon('focusout');
  const vvResize = soon('vv-resize');
  const onResize = () => {
    noteOpenHeight(); // a rotation grows it; a keyboard is only ever up with a field focused
    vvResize();
  };
  const onVisibility = soon('visibility');
  // A tap that lands on nothing (html/body) is what the offset looks like from the finger's side: reset at once.
  const onDeadTap = (e) => {
    const t = e.target;
    if (t === document.documentElement || t === document.body)
      resetDocumentScrollIfIdle('dead-tap');
  };
  const vv = window.visualViewport;
  document.addEventListener('focusout', onFocusOut, true);
  vv?.addEventListener('resize', onResize);
  document.addEventListener('visibilitychange', onVisibility);
  document.addEventListener('pointerdown', onDeadTap, true);
  // The three signals above all missed at least once (the dock went dead with none of them firing), so a
  // cheap periodic read backs them up while the app is on screen.
  const periodic = setInterval(() => {
    if (!document.hidden) resetDocumentScrollIfIdle('periodic');
  }, 3000);
  return () => {
    installed = false;
    if (timer != null) clearTimeout(timer);
    clearInterval(periodic);
    document.removeEventListener('focusout', onFocusOut, true);
    vv?.removeEventListener('resize', onResize);
    document.removeEventListener('visibilitychange', onVisibility);
    document.removeEventListener('pointerdown', onDeadTap, true);
  };
}
